/**
 * Vendors.
 *
 * Built on the same blueprint as Customers: lifecycle segments own the status
 * filter, the metric strip is made of links, and every filter and sort goes to
 * the server.
 *
 * The segment that matters most is "Awaiting review" — the KYC queue is this
 * module's standing job — so it carries a live count wherever you are, and the
 * page defaults to sorting that queue oldest-first, because the only fair order
 * to review applications in is the order they arrived.
 *
 * Deliberately absent: bulk approve. Every other list here gained a bulk
 * action, but approving KYC means having looked at that vendor's documents, and
 * a control that approves twenty businesses in one click is a control that
 * eventually approves twenty unread ones. Selection exists for copying ids.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  BadgeCheck,
  Ban,
  CheckCircle2,
  ClipboardCheck,
  Copy,
  ExternalLink,
  MapPin,
  MoreHorizontal,
  Sparkles,
  Star,
  Store,
  Wallet,
  XCircle,
} from 'lucide-react';
import {
  bool_param,
  enum_param,
  number_param,
  page_param,
  page_size_param,
  sort_param,
  string_param,
  use_url_state,
  type SortState,
} from '@/hooks/use-url-state';
import { DashboardLayout, PageTabs } from '@/components/dashboard-layout';
import { DataTable } from '@/components/data-table/data-table';
import {
  BoolFilter,
  FilterBar,
  NumberFilter,
  SearchInput,
  SelectFilter,
} from '@/components/filters/filter-bar';
import { DateRangeFilter, describe_range } from '@/components/filters/date-range-filter';
import { SavedViews } from '@/components/filters/saved-views';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { PersonAvatar } from '@/components/person-avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { bulk_btn } from '@/components/data-table/bulk-bar';
import { ReasonModal } from '@/features/vendors/components/reason-modal';
import { StatusBadge } from '@/features/vendors/components/status-badge';
import { notify } from '@/components/ui/toaster';
import { ago, date_short } from '@/lib/format';
import { export_vendors } from '@/features/vendors/api';
import {
  use_vendor_counts,
  use_vendor_list,
  use_vendor_status_actions,
} from '@/features/vendors/hooks';
import {
  MODULE_TYPE_OPTIONS,
  VENDOR_MODULE_TYPE_LABELS,
  VENDOR_SORT_FIELDS,
  type ListFilters,
  type VendorListItem,
  type VendorModuleType,
} from '@/features/vendors/types';
import { cn } from '@/lib/utils';

/**
 * Lifecycle segments.
 *
 * "Blocked" is two statuses, which is why the API grew a `statuses` filter —
 * rejected and suspended are the same thing to an operator scanning for
 * businesses that are off the platform, and splitting them into two tabs would
 * mean checking both every time.
 */
const SEGMENTS = ['all', 'queue', 'onboarding', 'live', 'blocked'] as const;
type Segment = (typeof SEGMENTS)[number];

const SEGMENT_STATUSES: Record<Segment, string | undefined> = {
  all: undefined,
  queue: 'DOCUMENTS_SUBMITTED',
  onboarding: 'PENDING',
  live: 'VERIFIED',
  blocked: 'REJECTED,SUSPENDED',
};

const SEGMENT_COPY: Record<Segment, { label: string; title: string; blurb: string }> = {
  all: {
    label: 'All vendors',
    title: 'Vendors',
    blurb: 'Every business on the platform, at any stage of onboarding',
  },
  queue: {
    label: 'Awaiting review',
    title: 'KYC review queue',
    blurb: 'Applications with documents submitted, oldest first',
  },
  onboarding: {
    label: 'Onboarding',
    title: 'Onboarding',
    blurb: 'Signed up but not yet submitted for review',
  },
  live: { label: 'Live', title: 'Live vendors', blurb: 'Verified businesses taking bookings' },
  blocked: {
    label: 'Blocked',
    title: 'Blocked vendors',
    blurb: 'Rejected or suspended — not visible to customers',
  },
};

/**
 * The queue is reviewed oldest-first; everywhere else, newest-first.
 *
 * `updated_at` ascending is "waiting longest": it is when the vendor last
 * touched the application, so it is the closest thing the schema has to a
 * submission time.
 */
const SEGMENT_DEFAULT_SORT: Record<Segment, SortState> = {
  all: null,
  queue: { by: 'updated_at', dir: 'asc' },
  onboarding: null,
  live: null,
  blocked: null,
};

export function VendorListPage() {
  const [segment_raw, set_segment] = use_url_state('segment', enum_param(SEGMENTS));
  const segment: Segment = segment_raw || 'all';
  const copy = SEGMENT_COPY[segment];
  const statuses = SEGMENT_STATUSES[segment];

  const [q, set_q] = use_url_state('q', string_param);
  const [module_type, set_module_type] = use_url_state(
    'module_type',
    enum_param(MODULE_TYPE_OPTIONS),
  );
  const [city, set_city] = use_url_state('city', string_param);
  const [state, set_state] = use_url_state('state', string_param);
  const [is_active, set_is_active] = use_url_state('is_active', bool_param);
  const [has_payout, set_has_payout] = use_url_state('has_payout', bool_param);
  const [min_rating, set_min_rating] = use_url_state('min_rating', number_param);
  const [from, set_from] = use_url_state('from', string_param);
  const [to, set_to] = use_url_state('to', string_param);
  const [page, set_page] = use_url_state('page', page_param);
  const [page_size, set_page_size] = use_url_state('page_size', page_size_param);
  const [sort_raw, set_sort] = use_url_state('sort', sort_param(VENDOR_SORT_FIELDS));

  // An untouched queue is ordered oldest-first without that showing up in the
  // URL; any explicit sort the operator picks wins.
  const sort = sort_raw ?? SEGMENT_DEFAULT_SORT[segment];

  const filters: ListFilters = {
    page,
    page_size,
    statuses,
    q: q.trim() || undefined,
    module_type: module_type || undefined,
    city: city.trim() || undefined,
    state: state.trim() || undefined,
    is_active: is_active || undefined,
    has_payout: has_payout || undefined,
    min_rating: min_rating ? Number(min_rating) : undefined,
    from: from || undefined,
    to: to || undefined,
  };

  const { data, isLoading, isFetching, error, refetch } = use_vendor_list(filters, sort);
  const counts = use_vendor_counts({ statuses });
  const actions = use_vendor_status_actions();

  /** Which vendor a reason-modal action is aimed at, and which action. */
  const [pending, set_pending] = useState<{
    vendor: VendorListItem;
    kind: 'reject' | 'suspend';
  } | null>(null);
  const [modal_error, set_modal_error] = useState<string | null>(null);

  const reset_page = () => set_page(1);

  function clear_all() {
    set_q('');
    set_module_type('');
    set_city('');
    set_state('');
    set_is_active('');
    set_has_payout('');
    set_min_rating('');
    set_from('');
    set_to('');
    reset_page();
  }

  const chips = [
    q.trim() && {
      key: 'q',
      label: 'Search',
      value: q.trim(),
      on_clear: () => {
        set_q('');
        reset_page();
      },
    },
    module_type && {
      key: 'module_type',
      label: 'Module',
      value: VENDOR_MODULE_TYPE_LABELS[module_type],
      on_clear: () => {
        set_module_type('');
        reset_page();
      },
    },
    city.trim() && {
      key: 'city',
      label: 'City',
      value: city.trim(),
      on_clear: () => {
        set_city('');
        reset_page();
      },
    },
    (from || to) && {
      key: 'date',
      label: 'Joined',
      value: describe_range(from, to),
      on_clear: () => {
        set_from('');
        set_to('');
        reset_page();
      },
    },
    state.trim() && {
      key: 'state',
      label: 'State',
      value: state.trim(),
      advanced: true,
      on_clear: () => {
        set_state('');
        reset_page();
      },
    },
    is_active && {
      key: 'is_active',
      label: 'Listing',
      value: is_active === 'true' ? 'Active' : 'Deactivated',
      advanced: true,
      on_clear: () => {
        set_is_active('');
        reset_page();
      },
    },
    has_payout && {
      key: 'has_payout',
      label: 'Payout',
      value: has_payout === 'true' ? 'Set up' : 'Missing',
      advanced: true,
      on_clear: () => {
        set_has_payout('');
        reset_page();
      },
    },
    min_rating && {
      key: 'min_rating',
      label: 'Rating',
      value: `${min_rating}+`,
      advanced: true,
      on_clear: () => {
        set_min_rating('');
        reset_page();
      },
    },
  ];

  const columns = useMemo<ColumnDef<VendorListItem, unknown>[]>(
    () => [
      {
        id: 'business_name',
        accessorFn: (v) => v.business_name ?? '',
        meta: {
          label: 'Business',
          sort_key: 'business_name',
          csv: (v) => v.business_name ?? '',
        },
        header: 'Business',
        cell: ({ row }) => {
          const v = row.original;
          const named = Boolean(v.business_name);
          return (
            <div className="flex min-w-0 items-center gap-2.5">
              {/* No vendor in the platform has uploaded a logo, so this is
                  initials rather than an image endpoint that would render an
                  empty square 135 times. */}
              <PersonAvatar
                path={null}
                name={v.business_name}
                seed={v.id}
                size="sm"
                fallback_icon={Store}
              />
              <span className="flex min-w-0 flex-col">
                <Link
                  to={`/vendors/${v.id}`}
                  className={cn(
                    'min-w-0 truncate underline-offset-2 hover:text-primary hover:underline',
                    named ? 'font-semibold text-foreground' : 'italic text-muted-foreground',
                  )}
                  data-testid={`vendor-row-link-${v.id}`}
                >
                  {v.business_name ?? 'Unnamed application'}
                </Link>
                <span className="truncate text-micro text-muted-foreground">
                  {v.owner_name ?? 'No owner name'}
                </span>
              </span>
            </div>
          );
        },
      },
      {
        id: 'module_types',
        accessorFn: (v) => v.module_types.join(' '),
        meta: {
          label: 'Modules',
          csv: (v) => v.module_types.map((m) => VENDOR_MODULE_TYPE_LABELS[m]).join(' | '),
        },
        header: 'Modules',
        cell: ({ row }) => {
          const mods = row.original.module_types;
          if (mods.length === 0) return <span className="text-muted-foreground/70">None yet</span>;
          // Two badges plus a +n, because a multi-module vendor would otherwise
          // make its row twice as tall as every other one.
          const shown = mods.slice(0, 2);
          const rest = mods.length - shown.length;
          return (
            <div className="flex flex-wrap items-center gap-1">
              {shown.map((m) => (
                <Badge key={m} tone="neutral" size="sm">
                  {VENDOR_MODULE_TYPE_LABELS[m]}
                </Badge>
              ))}
              {rest > 0 && (
                <Badge
                  tone="neutral"
                  variant="outline"
                  size="sm"
                  title={mods.map((m) => VENDOR_MODULE_TYPE_LABELS[m]).join(', ')}
                >
                  +{rest}
                </Badge>
              )}
            </div>
          );
        },
      },
      {
        id: 'city',
        accessorFn: (v) => v.city ?? '',
        meta: {
          label: 'Location',
          sort_key: 'city',
          csv: (v) => [v.city, v.state].filter(Boolean).join(', '),
        },
        header: 'Location',
        cell: ({ row }) => {
          const v = row.original;
          if (!v.city) return <span className="text-muted-foreground/70">Not set</span>;
          return (
            <div className="flex min-w-0 items-center gap-1.5">
              <MapPin aria-hidden="true" className="h-3 w-3 shrink-0 text-muted-foreground" />
              <span className="flex min-w-0 flex-col">
                <span className="truncate font-medium">{v.city}</span>
                {v.state && (
                  <span className="truncate text-micro text-muted-foreground">{v.state}</span>
                )}
              </span>
            </div>
          );
        },
      },
      {
        id: 'kyc_status',
        accessorFn: (v) => v.kyc_status,
        meta: { label: 'Status', sort_key: 'kyc_status', csv: (v) => v.kyc_status },
        header: 'Status',
        cell: ({ row }) => (
          <div className="flex flex-col items-start gap-1">
            <StatusBadge status={row.original.kyc_status} />
            {/* A verified vendor that has been deactivated is invisible to
                customers, which the KYC badge alone does not say. */}
            {!row.original.is_active && (
              <Badge tone="warning" size="sm">
                Deactivated
              </Badge>
            )}
          </div>
        ),
      },
      {
        id: 'rating',
        accessorFn: (v) => Number(v.avg_rating),
        meta: {
          label: 'Rating',
          sort_key: 'avg_rating',
          align: 'right',
          csv: (v) => v.avg_rating,
        },
        header: 'Rating',
        cell: ({ row }) => {
          const v = row.original;
          if (v.review_count === 0) {
            return <span className="text-muted-foreground/70">No reviews</span>;
          }
          return (
            <span className="inline-flex items-center gap-1 tabular">
              <Star aria-hidden="true" className="h-3 w-3 fill-warning text-warning" />
              <span className="font-semibold">{Number(v.avg_rating).toFixed(1)}</span>
              <span className="text-micro text-muted-foreground">({v.review_count})</span>
            </span>
          );
        },
      },
      {
        id: 'service_count',
        accessorFn: (v) => v.service_count,
        meta: { label: 'Services', align: 'right', csv: (v) => v.service_count },
        header: 'Services',
        cell: ({ row }) => (
          <span
            className={cn(
              'tabular',
              row.original.service_count === 0
                ? // Verified with nothing to book is a dead listing, so the
                  // zero is called out rather than dimmed.
                  row.original.kyc_status === 'VERIFIED'
                  ? 'font-semibold text-warning'
                  : 'text-muted-foreground/60'
                : 'font-semibold',
            )}
          >
            {row.original.service_count}
          </span>
        ),
      },
      {
        id: 'has_payout',
        accessorFn: (v) => v.has_payout,
        meta: {
          label: 'Payout',
          align: 'center',
          csv: (v) => (v.has_payout ? 'Yes' : 'No'),
        },
        header: 'Payout',
        cell: ({ row }) => {
          const v = row.original;
          if (v.has_payout) {
            return (
              <BadgeCheck
                aria-label="Payout details on file"
                className="mx-auto h-3.5 w-3.5 text-success"
              />
            );
          }
          // Only alarming once they are live and actually earning.
          const critical = v.kyc_status === 'VERIFIED';
          return (
            <XCircle
              aria-label={critical ? 'Verified but cannot be paid' : 'No payout details yet'}
              className={cn(
                'mx-auto h-3.5 w-3.5',
                critical ? 'text-destructive' : 'text-muted-foreground/50',
              )}
            />
          );
        },
      },
      {
        id: 'kyc_document_count',
        accessorFn: (v) => v.kyc_document_count,
        meta: {
          label: 'Docs',
          align: 'right',
          csv: (v) => v.kyc_document_count,
          hidden_by_default: true,
        },
        header: 'Docs',
        cell: ({ row }) => (
          <span
            className={cn(
              'tabular',
              row.original.kyc_document_count === 0 ? 'text-muted-foreground/60' : 'font-semibold',
            )}
          >
            {row.original.kyc_document_count}
          </span>
        ),
      },
      {
        id: 'updated_at',
        accessorFn: (v) => v.updated_at,
        meta: { label: 'Last activity', sort_key: 'updated_at', csv: (v) => v.updated_at },
        header: 'Last activity',
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground">
            {ago(row.original.updated_at)}
          </span>
        ),
      },
      {
        id: 'created_at',
        accessorFn: (v) => v.created_at,
        meta: {
          label: 'Joined',
          sort_key: 'created_at',
          csv: (v) => v.created_at,
          hidden_by_default: true,
        },
        header: 'Joined',
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground">
            {date_short(row.original.created_at)}
          </span>
        ),
      },
    ],
    [],
  );

  const seg_link = (next: Segment, extra = '') =>
    `/vendors?segment=${next}${extra ? `&${extra}` : ''}`;
  const c = counts.data;

  const metrics: Metric[] = [
    {
      key: 'total',
      label: segment === 'queue' ? 'In this queue' : copy.label,
      value: c?.total?.toLocaleString('en-IN'),
      icon: Store,
      to: seg_link(segment),
      loading: counts.isLoading,
    },
    // Inside the queue, the segment total IS the awaiting-review count, and
    // two tiles side by side showing the same number reads as a bug. That slot
    // shows what is off the platform instead.
    segment === 'queue'
      ? {
          key: 'blocked',
          label: 'Blocked',
          value: c?.blocked?.toLocaleString('en-IN'),
          hint: 'Rejected or suspended',
          icon: Ban,
          tone: c && c.blocked > 0 ? 'warning' : 'default',
          to: seg_link('blocked'),
          loading: counts.isLoading,
        }
      : {
          key: 'awaiting_review',
          label: 'Awaiting review',
          value: c?.awaiting_review?.toLocaleString('en-IN'),
          hint: 'Documents submitted',
          icon: ClipboardCheck,
          tone: c && c.awaiting_review > 0 ? 'warning' : 'default',
          to: seg_link('queue'),
          loading: counts.isLoading,
        },
    {
      key: 'live',
      label: 'Live',
      value: c?.live?.toLocaleString('en-IN'),
      hint: 'Verified and listed',
      icon: CheckCircle2,
      tone: 'success',
      to: seg_link('live'),
      loading: counts.isLoading,
    },
    {
      key: 'unpayable',
      label: 'Cannot be paid',
      value: c?.unpayable?.toLocaleString('en-IN'),
      hint: 'Verified, no payout details',
      icon: Wallet,
      tone: c && c.unpayable > 0 ? 'destructive' : 'default',
      to: seg_link('live', 'has_payout=false'),
      loading: counts.isLoading,
    },
    {
      key: 'new',
      label: 'Joined this month',
      value: c?.joined_this_month?.toLocaleString('en-IN'),
      icon: Sparkles,
      tone: 'primary',
      loading: counts.isLoading,
    },
  ];

  function approve(v: VendorListItem) {
    actions.approve.mutate(v.id, {
      onSuccess: () =>
        notify.success(`${v.business_name ?? 'Vendor'} approved`, {
          description: 'They can now be found by customers and take bookings.',
        }),
      onError: (err) => notify.error(err, 'Could not approve this vendor.'),
    });
  }

  function commit_reason(reason: string) {
    if (!pending) return;
    const { vendor, kind } = pending;
    const on_done = {
      onSuccess: () => {
        set_pending(null);
        notify.success(
          kind === 'reject'
            ? `${vendor.business_name ?? 'Vendor'} rejected`
            : `${vendor.business_name ?? 'Vendor'} suspended`,
          { description: 'The reason is recorded in their KYC history.' },
        );
      },
      onError: (err: unknown) => {
        set_modal_error(err instanceof Error ? err.message : 'Could not update this vendor');
        notify.error(err, 'Could not update this vendor.');
      },
    };
    if (kind === 'reject') actions.reject.mutate({ id: vendor.id, reason }, on_done);
    else actions.suspend.mutate({ id: vendor.id, reason }, on_done);
  }

  return (
    <DashboardLayout
      title={copy.title}
      subtitle={copy.blurb}
      actions={<SavedViews module={`vendors:${segment}`} is_filtered={chips.some(Boolean)} />}
      tabs={
        <PageTabs
          current={segment}
          on_change={(next) => {
            set_segment(next as Segment);
            // A sort chosen for one segment is rarely right for the next, and
            // the queue has its own default.
            set_sort(null);
            reset_page();
          }}
          tabs={SEGMENTS.map((v) => ({
            value: v,
            label: SEGMENT_COPY[v].label,
            // The queue's size is the one number worth carrying on the tab.
            count: v === 'queue' ? c?.awaiting_review : undefined,
          }))}
        />
      }
      stats={<MetricStrip metrics={metrics} />}
    >
      <DataTable<VendorListItem>
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        get_row_id={(v) => v.id}
        page={page}
        page_size={page_size}
        on_page_change={set_page}
        on_page_size_change={(n) => {
          set_page_size(n);
          reset_page();
        }}
        sort={sort}
        on_sort_change={(next) => {
          set_sort(next);
          reset_page();
        }}
        is_loading={isLoading}
        is_fetching={isFetching}
        error={error}
        on_retry={() => void refetch()}
        row_href={(v) => `/vendors/${v.id}`}
        storage_key={`vendors:${segment}`}
        export_name="vendors"
        on_export_all={() => export_vendors(filters, sort)}
        caption="Vendor businesses"
        test_id="vendor-list"
        selectable
        empty={{
          icon: Store,
          title: chips.some(Boolean)
            ? `No ${copy.label.toLowerCase()} match these filters`
            : segment === 'queue'
              ? 'Nothing waiting on review'
              : `No ${copy.label.toLowerCase()} yet`,
          description: chips.some(Boolean)
            ? 'Try widening the date range or clearing a filter.'
            : segment === 'queue'
              ? 'Applications appear here once a vendor submits their documents.'
              : 'Businesses appear here as they sign up in the vendor app.',
        }}
        filters={
          <FilterBar
            chips={chips}
            on_clear_all={clear_all}
            advanced={
              <>
                <SearchInput
                  value={state}
                  on_change={(next) => {
                    set_state(next);
                    reset_page();
                  }}
                  label="State"
                  placeholder="Karnataka…"
                  test_id="filter-state"
                  className="!flex-none !basis-auto"
                />
                <BoolFilter
                  label="Listing"
                  value={is_active}
                  on_change={(v) => {
                    set_is_active(v);
                    reset_page();
                  }}
                  true_label="Active"
                  false_label="Deactivated"
                  test_id="filter-is-active"
                />
                <BoolFilter
                  label="Payout details"
                  value={has_payout}
                  on_change={(v) => {
                    set_has_payout(v);
                    reset_page();
                  }}
                  true_label="On file"
                  false_label="Missing"
                  test_id="filter-has-payout"
                />
                <NumberFilter
                  label="Min rating"
                  value={min_rating}
                  on_change={(v) => {
                    set_min_rating(v);
                    reset_page();
                  }}
                  placeholder="4"
                  test_id="filter-min-rating"
                />
              </>
            }
          >
            <SearchInput
              value={q}
              on_change={(next) => {
                set_q(next);
                reset_page();
              }}
              label="Search"
              placeholder="Business or owner name…"
              test_id="filter-q"
            />
            <SelectFilter
              label="Module"
              value={module_type}
              on_change={(next) => {
                set_module_type(next as VendorModuleType | '');
                reset_page();
              }}
              options={MODULE_TYPE_OPTIONS.map((m) => ({
                value: m,
                label: VENDOR_MODULE_TYPE_LABELS[m],
              }))}
              all_label="All modules"
              test_id="filter-module"
              width="12rem"
            />
            <SearchInput
              value={city}
              on_change={(next) => {
                set_city(next);
                reset_page();
              }}
              label="City"
              placeholder="Bangalore…"
              test_id="filter-city"
              className="!flex-none !basis-auto"
            />
            <DateRangeFilter
              label="Joined"
              from={from}
              to={to}
              on_change={(next) => {
                set_from(next.from);
                set_to(next.to);
                reset_page();
              }}
            />
          </FilterBar>
        }
        row_actions={(v) => (
          <DropdownMenu>
            <DropdownMenuTrigger
              className="inline-flex h-7 w-7 items-center justify-center rounded-control text-muted-foreground opacity-60 transition-all hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover/row:opacity-100"
              aria-label={`Actions for ${v.business_name ?? 'this vendor'}`}
              data-testid={`row-actions-${v.id}`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to={`/vendors/${v.id}`}>
                  <ExternalLink className="h-4 w-4" />
                  Open vendor
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  void navigator.clipboard.writeText(v.id);
                  notify.success('Vendor ID copied');
                }}
              >
                <Copy className="h-4 w-4" />
                Copy vendor ID
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {/*
                Approve is offered from the row only for an application that has
                actually submitted documents, and it still says "review" — the
                menu item opens nothing, so the wording has to make clear this
                is the decision, not a shortcut past it.
              */}
              {v.kyc_status === 'DOCUMENTS_SUBMITTED' && (
                <>
                  <DropdownMenuItem asChild>
                    <Link to={`/vendors/${v.id}`}>
                      <ClipboardCheck className="h-4 w-4" />
                      Review documents
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={actions.approve.isPending}
                    onSelect={() => approve(v)}
                    data-testid={`approve-${v.id}`}
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Approve
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    tone="destructive"
                    onSelect={() => {
                      set_modal_error(null);
                      set_pending({ vendor: v, kind: 'reject' });
                    }}
                    data-testid={`reject-${v.id}`}
                  >
                    <XCircle className="h-4 w-4" />
                    Reject…
                  </DropdownMenuItem>
                </>
              )}
              {v.kyc_status === 'VERIFIED' && (
                <DropdownMenuItem
                  tone="destructive"
                  onSelect={() => {
                    set_modal_error(null);
                    set_pending({ vendor: v, kind: 'suspend' });
                  }}
                  data-testid={`suspend-${v.id}`}
                >
                  <Ban className="h-4 w-4" />
                  Suspend…
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        bulk_actions={({ ids, clear }) => (
          <>
            <button
              type="button"
              className={bulk_btn}
              onClick={() => {
                void navigator.clipboard.writeText(ids.join('\n'));
                notify.success(`${ids.length} vendor IDs copied`);
                clear();
              }}
            >
              <Copy />
              Copy IDs
            </button>
            {/* No bulk approve, on purpose — see the note at the top of this
                file. The bar says so rather than leaving its absence to be
                read as an oversight. */}
            <span className="px-1 text-micro opacity-80">
              KYC decisions are one vendor at a time
            </span>
          </>
        )}
      />

      <ReasonModal
        open={pending !== null}
        title={
          pending?.kind === 'reject'
            ? `Reject ${pending.vendor.business_name ?? 'this application'}?`
            : `Suspend ${pending?.vendor.business_name ?? 'this vendor'}?`
        }
        description={
          pending?.kind === 'reject'
            ? 'The vendor is told their application was rejected and can resubmit. The reason is recorded in their KYC history.'
            : 'The vendor is removed from customer search immediately and cannot take new bookings. Existing bookings are unaffected.'
        }
        confirm_label={pending?.kind === 'reject' ? 'Reject application' : 'Suspend vendor'}
        variant="destructive"
        loading={actions.reject.isPending || actions.suspend.isPending}
        error={modal_error}
        on_cancel={() => set_pending(null)}
        on_confirm={commit_reason}
      />
    </DashboardLayout>
  );
}
